/**
 * Plan Mode types — mirrors agent-service/backend/src/types/plan.ts
 * for frontend consumption. Includes ALL types from Phase 1:
 * - Original: PlanSectionDomain, PlanStatus, PlanSectionStatus, PlanSectionDetails,
 *   PlanSection, Plan, PlanConversationResponse, PlanCompilationResponse
 * - Full Mode: PlanPipelineMode, PlanPipelineStage, ResearchFinding, PlanResearchContext,
 *   RoadmapPhase, Roadmap, AuditCriterion, AuditResult, PhasePrompt, QualityGateBlock
 */

// ============================================================
// CORE TYPES
// ============================================================

/** The 4 supported plan section domains */
export type PlanSectionDomain = 'visual-editor' | 'data' | 'workflow' | 'interaction';

/** Plan lifecycle status — Quick Mode uses subset, Full Mode uses all */
export type PlanStatus = 'drafting' | 'ready' | 'approved' | 'executing' | 'completed' | 'failed';

/** Pipeline mode toggle */
export type PlanPipelineMode = 'quick' | 'full' | 'skill' | 'agent' | 'knowledge' | 'workflow' | 'data' | 'page-design';

/** Proposed skill structure from the skill wizard */
export interface ProposedSkill {
  name: string;
  description: string;
  instructions: string;
  domain?: 'data' | 'workflow' | 'general';
  tags?: string[];
  triggers?: Array<{ type: string; config: Record<string, unknown> }>;
  capabilities?: string[];
}

/** Proposed agent from the agent wizard */
export interface ProposedAgent {
  name: string;
  description: string;
  role: string;
  capabilities?: string[];
  tools?: string[];
  domain?: string;
  mqttSubscriptions?: string[];
  tier?: number;
}

/** Proposed knowledge base from the knowledge wizard */
export interface ProposedKnowledgeBase {
  title: string;
  description: string;
  domain: string;
  articles?: Array<{ title: string; outline: string; purpose_category?: string }>;
  sources?: string[];
  tags?: string[];
  gaps?: Array<{
    topic: string;
    description: string;
    category: string;
    gap_type: 'discrete' | 'evolving';
    priority: 'blocking' | 'important' | 'nice_to_have';
    suggested_question: string;
  }>;
  completeness_estimate?: number;
}

/** Proposed workflow from the workflow wizard */
export type AutomationRiskLevel =
  | 'informational'
  | 'low'
  | 'review_required'
  | 'blocked';

export interface AutomationProposalSafety {
  riskLevel: AutomationRiskLevel;
  riskReasons: string[];
  approvalRequired: boolean;
  draftByDefault: boolean;
  disabledByDefault: boolean;
  triggerSummary: string;
  conditionSummary?: string;
  affectedDevices: string[];
  affectedEntities: string[];
  actionSummary: string;
  scheduleSummary?: string;
  rollbackNote: string;
}

export interface ProposedWorkflow {
  name: string;
  description: string;
  triggerType: string;
  triggerConfig?: Record<string, unknown>;
  steps?: Array<{ type: string; description: string }>;
  tags?: string[];
  safety?: AutomationProposalSafety;
}

/** Proposed data schema from the data wizard */
export interface ProposedSchema {
  name: string;
  description: string;
  groupName: string;
  fields?: Array<{ name: string; type: string; required?: boolean; description?: string }>;
  tags?: string[];
}

/** Proposed page design from the page design wizard */
export interface ProposedPageDesign {
  pages: Array<{
    name: string;
    purpose: string;
    layout: string;
    components: string[];
    dataBindings?: string[];
  }>;
  navigation?: string;
  sharedComponents?: string[];
}

/** Full pipeline stages (for progress tracking) */
export type PlanPipelineStage =
  | 'idle' | 'conversing' | 'researching'
  | 'generating_roadmap' | 'auditing_roadmap'
  | 'decomposing' | 'auditing_phases'
  | 'reviewing'
  | 'executing_phase' | 'auditing_implementation'
  | 'final_audit' | 'complete' | 'failed';

/** Execution status for individual plan sections */
export type PlanSectionStatus = 'pending' | 'executing' | 'completed' | 'failed' | 'skipped';

// ============================================================
// DOMAIN-SPECIFIC SECTION DETAILS (Discriminated Union)
// ============================================================

/** Domain-specific details for each plan section (discriminated union on `domain`) */
export interface VisualEditorSectionDetails {
  domain: 'visual-editor';
  layoutDescription: string;
  componentList: string[];
  breakpointNotes?: string;
}

export interface DataSectionDetails {
  domain: 'data';
  schemas: string[];
  bindings: string[];
  queryDescriptions?: string[];
}

export interface WorkflowSectionDetails {
  domain: 'workflow';
  triggers: string[];
  actions: string[];
  conditions?: string[];
}

export interface InteractionSectionDetails {
  domain: 'interaction';
  triggers: string[];
  actions: string[];
  targetComponents?: string[];
}

export type PlanSectionDetails =
  | VisualEditorSectionDetails
  | DataSectionDetails
  | WorkflowSectionDetails
  | InteractionSectionDetails;

// ============================================================
// PLAN AND SECTION INTERFACES
// ============================================================

/** A single section of the plan */
export interface PlanSection {
  id: string;
  domain: PlanSectionDomain;
  title: string;
  description: string;
  executionPrompt: string;
  details: PlanSectionDetails;
  dependsOn: string[];                    // IDs of sections that must complete first
  status: PlanSectionStatus;
  artifacts?: Record<string, string[]>;   // e.g., { componentIds: ['id1', 'id2'], schemaIds: [...] }
  pageName?: string;                      // For multi-page plans: page name for visual-editor sections
  /** @deprecated Legacy backend field name for executionPrompt. Read-only; do not write. */
  prompt?: string;
  /** @deprecated Legacy phase numbering. Read-only; do not write. */
  phaseNumber?: number;
}

/** The complete plan object */
export interface Plan {
  id: string;
  threadId: string;
  status: PlanStatus;
  sections: PlanSection[];
  summary: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================================
// CONVERSATION RESPONSE TYPES
// ============================================================

/** A single option within a structured plan question */
export interface PlanQuestionOption {
  label: string;
  description?: string;
}

/** A structured question with clickable options (rendered as interactive cards in the chat) */
export interface PlanQuestion {
  question: string;
  options: PlanQuestionOption[];
  multiSelect: boolean;
}

/** Response from a single planning conversation turn */
export interface PlanConversationResponse {
  message: string;
  planReady: boolean;                     // true when AI has gathered enough context
  questions?: PlanQuestion[];             // structured questions with clickable options
  discoveredSchemas?: string[];           // schema names found during data discovery
  researchSuggestions?: string[];         // topics the AI wants to research (Full Mode)
  researchFindings?: ResearchFinding[];   // findings from web research (injected by PlanService)
  threadId: string;
  turnIndex: number;
}

/** Response from plan compilation (Quick Mode) */
export interface PlanCompilationResponse {
  plan: Plan;
  threadId: string;
}

// ============================================================
// WEB RESEARCH TYPES (Full Mode)
// ============================================================

/** A single research finding from web scraping */
export interface ResearchFinding {
  id: string;
  topic: string;
  url: string;
  title: string;
  summary: string;                        // LLM-generated summary of relevant content
  relevanceScore: number;                 // 0-1, LLM-assessed relevance to the plan
  sourceType: 'domain_data' | 'ux_best_practice' | 'technical_reference' | 'design_inspiration';
  rawContentPreview: string;              // first 500 chars of scraped content
  extractedAt: string;                    // ISO timestamp
}

/** Aggregated research context for roadmap generation */
export interface PlanResearchContext {
  findings: ResearchFinding[];
  topicsCovered: string[];
  totalUrlsScraped: number;
  synthesizedInsight: string;             // LLM-generated cross-finding synthesis
}

// ============================================================
// ROADMAP TYPES (Full Mode)
// ============================================================

/** A single phase in the roadmap */
export interface RoadmapPhase {
  id: string;
  phaseNumber: number;
  title: string;
  description: string;
  domain: PlanSectionDomain;
  tasks: string[];
  dependsOn: string[];                    // phase IDs
  estimatedComplexity: 'low' | 'medium' | 'high';
  acceptanceCriteria: string[];
  researchFindings?: string[];            // relevant ResearchFinding IDs
}

/** The complete roadmap object */
export interface Roadmap {
  id: string;
  threadId: string;
  title: string;
  summary: string;
  phases: RoadmapPhase[];
  estimatedTotalDuration: string;         // e.g., "3-5 minutes execution"
  estimatedLLMCalls: number;
  estimatedCost?: number;                 // Estimated USD cost (computed by PlanService from COST_TABLE)
  researchContext?: PlanResearchContext;
  createdAt: string;
  updatedAt: string;
}

// ============================================================
// SAVED ROADMAP TYPES
// ============================================================

/** Lightweight summary for listing saved roadmaps */
export interface SavedRoadmapSummary {
  id: string;
  title: string;
  phaseCount: number;
  auditScore?: number;
  createdAt: string;
}

// ============================================================
// AUDIT TYPES (Full Mode)
// ============================================================

/** A single audit criterion evaluation */
/** A single feedback item with LLM-tagged sentiment */
export interface FeedbackItem {
  text: string;
  sentiment: 'positive' | 'negative' | 'neutral';
}

export interface AuditCriterion {
  name: string;                           // e.g., "Completeness", "Feasibility"
  verdict: 'pass' | 'warn' | 'fail';
  score: number;                          // 0-100
  reasoning: string;                      // joined text (backward compat)
  feedback?: FeedbackItem[];              // structured items with sentiment
  suggestions: string[];
}

/** Result of any audit operation */
export interface AuditResult {
  id: string;
  auditType: 'roadmap' | 'phase_prompt' | 'implementation' | 'final';
  targetId: string;                       // roadmap ID or phase ID
  verdict: 'pass' | 'warn' | 'fail';
  overallScore: number;                   // 0-100
  criteria: AuditCriterion[];
  blockingIssues: string[];
  warnings: string[];
  suggestions: string[];
  auditedAt: string;
  modelUsed: string;
  retryCount: number;
}

// ============================================================
// PHASE DECOMPOSITION TYPES (Full Mode)
// ============================================================

/** A self-contained execution prompt for a single phase */
export interface PhasePrompt {
  id: string;
  phaseId: string;                        // references RoadmapPhase.id
  phaseNumber: number;
  title: string;
  domain: PlanSectionDomain;
  executionPrompt: string;                // the full prompt sent to the execution pipeline
  dependsOn: string[];                    // phase prompt IDs
  expectedArtifacts: string[];            // e.g., ['componentIds', 'schemaIds']
  enrichmentTokens: string[];             // e.g., ['{{section:visual-editor:componentIds}}']
  estimatedDuration: string;
  pageName?: string;
  auditResult?: AuditResult;
  /** @deprecated use executionPrompt — kept for backward compat with older backends */
  prompt?: string;
}

// ============================================================
// QUALITY GATE TYPES
// ============================================================

/** Blocking quality gate event (pauses pipeline for user decision) */
export interface QualityGateBlock {
  phaseId?: string;
  reason: string;
  suggestedAction: 'retry' | 'skip' | 'return_to_planning';
  suggestedMessage?: string;              // Human-readable explanation
  auditResult: AuditResult;
}

// ============================================================
// DEBUG TYPES
// ============================================================

/** A single LLM call captured by the debug pipeline */
export interface PlanLLMDebugCall {
  stage: string;            // 'converse' | 'compile' | 'roadmap_generate' | 'roadmap_audit' | ...
  targetId?: string;        // For per-phase calls: 'phase-1', etc.
  systemPromptName: string;
  systemPrompt: string;
  userPrompt: string;
  rawResponse?: string;     // First 2000 chars
}

// ============================================================
// UI CONSTANTS (frontend-only)
// ============================================================

/** Domain icon mapping for UI (Lucide icon names) */
export const PLAN_DOMAIN_ICONS: Record<PlanSectionDomain, string> = {
  'visual-editor': 'Layout',
  'data': 'Database',
  'workflow': 'GitBranch',
  'interaction': 'MousePointerClick',
};

/** Domain display names */
export const PLAN_DOMAIN_LABELS: Record<PlanSectionDomain, string> = {
  'visual-editor': 'Visual Layout',
  'data': 'Data & Schemas',
  'workflow': 'Workflows',
  'interaction': 'Interactions',
};

/** Pipeline stage display names */
export const PIPELINE_STAGE_LABELS: Record<PlanPipelineStage, string> = {
  idle: 'Ready',
  conversing: 'Planning Conversation',
  researching: 'Researching Topics',
  generating_roadmap: 'Generating Roadmap',
  auditing_roadmap: 'Auditing Roadmap',
  decomposing: 'Decomposing Phases',
  auditing_phases: 'Auditing Phase Prompts',
  reviewing: 'Awaiting Review',
  executing_phase: 'Executing Phase',
  auditing_implementation: 'Auditing Implementation',
  final_audit: 'Final Audit',
  complete: 'Complete',
  failed: 'Failed',
};

/** Stages where the pipeline is idle — waiting for user action, not doing LLM work */
export const IDLE_PIPELINE_STAGES: ReadonlySet<PlanPipelineStage> = new Set([
  'idle',
  'conversing',
  'reviewing',
  'failed',
  'complete',
]);

/** Audit verdict colors (design token references) */
export const AUDIT_VERDICT_COLORS = {
  pass: 'FOREST_GREEN.DEFAULT',
  warn: 'WARNING.DEFAULT',
  fail: 'ERROR.DEFAULT',
} as const;

/** Research source type icons (Lucide icon names) */
export const RESEARCH_SOURCE_ICONS: Record<ResearchFinding['sourceType'], string> = {
  domain_data: 'BookOpen',
  ux_best_practice: 'Lightbulb',
  technical_reference: 'Code',
  design_inspiration: 'Palette',
};
